/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import React, { useState, useEffect, useMemo, useCallback } from "react"
import {
  Modal,
  Button,
  Tag,
  Typography,
  Spin,
  message,
  Empty,
  Switch,
  Tabs,
  Input,
  Select,
  Alert,
  Tooltip,
} from "antd"
import {
  HolderOutlined,
  FileTextOutlined,
  FolderOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  FormatPainterOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { debounce } from "lodash-es"
import { settingService, type SettingItem } from "@/services/system/settingApi"
import { setService } from "@/services/dictionary/setApi"

const { Text, Paragraph } = Typography
const { TextArea } = Input

interface EditSettingModalProps {
  open: boolean
  setting: SettingItem | null
  onClose: () => void
  onSuccess: () => void
}

interface SortableSetItemProps {
  item: any
  index: number
  totalCount: number
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
}

const SortableSetItem: React.FC<SortableSetItemProps> = ({
  item,
  index,
  totalCount,
  onMoveUp,
  onMoveDown,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 9999 : "auto",
    opacity: isDragging ? 0.75 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center justify-between gap-3 p-3 mb-2 rounded-lg border bg-white transition-all ${
        isDragging
          ? "border-primary shadow-lg bg-blue-50/50"
          : "border-gray-200 hover:border-gray-300 hover:shadow-xs"
      }`}
    >
      {/* Left: Drag handle & index & info */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div
          {...attributes}
          {...listeners}
          className="flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing rounded hover:bg-gray-100"
          title="Kéo thả để đổi thứ tự"
        >
          <HolderOutlined className="text-base" />
        </div>

        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
          {index + 1}
        </div>

        <div className="shrink-0 text-base">
          {item.thumbnail ? (
            <img
              src={item.thumbnail}
              alt=""
              className="w-8 h-8 rounded object-cover border border-gray-200"
            />
          ) : (
            <div className="w-8 h-8 rounded bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
              <FolderOutlined className="text-sm" />
            </div>
          )}
        </div>

        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-800 text-sm truncate max-w-[360px]">
              {item.full_title || item.title || `Thư mục ID #${item.id}`}
            </span>
            <Tag color="geekblue" className="text-[11px] m-0 py-0 px-1 font-mono">
              ID: {item.id}
            </Tag>
            {item.total_words !== undefined && (
              <Tag color="orange" className="text-[11px] m-0 py-0 px-1">
                {item.total_words} từ
              </Tag>
            )}
            {Array.isArray(item.tags) &&
              item.tags.map((t: string) => (
                <Tag key={t} color="cyan" className="text-[10px] m-0 py-0 px-1">
                  {t}
                </Tag>
              ))}
          </div>
          {item.full_title && item.title && item.full_title !== item.title && (
            <Text type="secondary" className="text-xs italic truncate max-w-[400px]">
              Tên ngắn: {item.title}
            </Text>
          )}
        </div>
      </div>

      {/* Right: Quick Up/Down buttons & remove */}
      <div className="flex items-center gap-1 shrink-0">
        <Tooltip title="Di chuyển lên">
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={(e) => {
              e.stopPropagation()
              onMoveUp()
            }}
            className="text-gray-500 hover:text-primary"
          />
        </Tooltip>
        <Tooltip title="Di chuyển xuống">
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === totalCount - 1}
            onClick={(e) => {
              e.stopPropagation()
              onMoveDown()
            }}
            className="text-gray-500 hover:text-primary"
          />
        </Tooltip>
        <Tooltip title="Xóa khỏi danh sách">
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          />
        </Tooltip>
      </div>
    </div>
  )
}

export const EditSettingModal: React.FC<EditSettingModalProps> = ({
  open,
  setting,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Locked state
  const [locked, setLocked] = useState(0)

  // Sets mode state
  const isSetsGroup = setting?.group === "sets"
  const [setItems, setSetItems] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<string>("visual")

  // Generic JSON editor state
  const [jsonText, setJsonText] = useState("")
  const [jsonError, setJsonError] = useState<string | null>(null)

  // Set Search State
  const [searchingSets, setSearchingSets] = useState(false)
  const [setOptions, setSetOptions] = useState<{ label: string; value: number; item: any }[]>([])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Initialize or fetch details on modal open
  useEffect(() => {
    if (!open || !setting) return

    setLocked(setting.locked || 0)
    setJsonError(null)

    if (setting.group === "sets") {
      setLoading(true)
      settingService
        .get(setting.id)
        .then((res) => {
          const detail = res.data
          setLocked(detail.locked || 0)
          const items = detail.set_items || []
          setSetItems(items)
          const ids = items.map((it) => it.id)
          setJsonText(JSON.stringify(ids, null, 2))
        })
        .catch((err) => {
          message.error("Lỗi khi tải chi tiết cấu hình: " + (err.message || "Unknown error"))
          // Fallback to record payload
          if (Array.isArray(setting.payload)) {
            const fallbackItems = setting.payload.map((id: number) => ({ id }))
            setSetItems(fallbackItems)
            setJsonText(JSON.stringify(setting.payload, null, 2))
          }
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      // Non-sets group
      try {
        setJsonText(JSON.stringify(setting.payload, null, 2))
      } catch {
        setJsonText(String(setting.payload))
      }
    }
  }, [open, setting])

  // Search sets handler
  const fetchSets = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query || query.trim().length === 0) {
          setSetOptions([])
          return
        }
        setSearchingSets(true)
        try {
          const res: any = await setService.list({
            page: 1,
            limit: 20,
            search: query.trim(),
            node_type: "folder",
            type: "system",
          })
          const list = res.data || []
          setSetOptions(
            list.map((s: any) => ({
              label: `[#${s.id}] ${s.full_title || s.title}`,
              value: s.id,
              item: s,
            }))
          )
        } catch (e: any) {
          message.error("Lỗi tìm kiếm thư mục: " + (e.message || "Unknown"))
        } finally {
          setSearchingSets(false)
        }
      }, 350),
    []
  )

  const handleAddSet = (value: number) => {
    const selected = setOptions.find((opt) => opt.value === value)?.item
    if (!selected) return

    if (setItems.some((item) => Number(item.id) === Number(value))) {
      message.warning(`Thư mục #${value} đã có trong danh sách!`)
      return
    }

    const updated = [...setItems, selected]
    setSetItems(updated)
    setJsonText(JSON.stringify(updated.map((it) => it.id), null, 2))
    message.success(`Đã thêm thư mục: ${selected.full_title || selected.title}`)
  }

  // Drag and drop handler
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setSetItems((items) => {
        const oldIndex = items.findIndex((it) => it.id === active.id)
        const newIndex = items.findIndex((it) => it.id === over.id)
        const updated = arrayMove(items, oldIndex, newIndex)
        setJsonText(JSON.stringify(updated.map((it) => it.id), null, 2))
        return updated
      })
    }
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const updated = arrayMove(setItems, index, index - 1)
    setSetItems(updated)
    setJsonText(JSON.stringify(updated.map((it) => it.id), null, 2))
  }

  const handleMoveDown = (index: number) => {
    if (index === setItems.length - 1) return
    const updated = arrayMove(setItems, index, index + 1)
    setSetItems(updated)
    setJsonText(JSON.stringify(updated.map((it) => it.id), null, 2))
  }

  const handleRemove = (index: number) => {
    const updated = setItems.filter((_, idx) => idx !== index)
    setSetItems(updated)
    setJsonText(JSON.stringify(updated.map((it) => it.id), null, 2))
  }

  // Format JSON action
  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(jsonText)
      setJsonText(JSON.stringify(parsed, null, 2))
      setJsonError(null)
      message.success("Đã định dạng JSON!")
    } catch (e: any) {
      setJsonError(`Lỗi cú pháp JSON: ${e.message}`)
    }
  }

  // Handle Raw JSON change in sets mode
  const handleRawJsonChange = (val: string) => {
    setJsonText(val)
    try {
      const parsed = JSON.parse(val)
      setJsonError(null)
      if (Array.isArray(parsed) && isSetsGroup) {
        // Sync to setItems if IDs changed
        const currentIds = setItems.map((it) => it.id)
        const hasDiff =
          parsed.length !== currentIds.length ||
          parsed.some((id, idx) => Number(id) !== Number(currentIds[idx]))
        if (hasDiff) {
          const itemMap = new Map(setItems.map((it) => [Number(it.id), it]))
          const newItems = parsed.map((id) => itemMap.get(Number(id)) || { id: Number(id) })
          setSetItems(newItems)
        }
      }
    } catch (e: any) {
      setJsonError(`Lỗi cú pháp JSON: ${e.message}`)
    }
  }

  // Save handler
  const handleSave = async () => {
    if (!setting) return

    let finalPayload: any
    try {
      finalPayload = JSON.parse(jsonText)
    } catch (e: any) {
      setJsonError(`JSON không hợp lệ: ${e.message}`)
      message.error("Vui lòng sửa các lỗi cú pháp JSON trước khi lưu!")
      return
    }

    setSaving(true)
    try {
      await settingService.update(setting.id, {
        payload: finalPayload,
        locked,
      })
      message.success("Cập nhật cấu hình thành công!")
      onSuccess()
      onClose()
    } catch (e: any) {
      message.error("Lưu cấu hình thất bại: " + (e.message || "Unknown error"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <span>Chỉnh sửa Cấu hình:</span>
          <span className="font-mono text-primary font-bold">{setting?.name}</span>
          <Tag color="geekblue" className="text-xs">
            {setting?.group}
          </Tag>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={780}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Hủy
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={loading || Boolean(jsonError)}
        >
          Lưu cấu hình
        </Button>,
      ]}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div className="flex flex-col gap-4 py-2">
          {/* Cache Notice Alert */}
          <Alert
            message="Đồng bộ Cache Tự Động"
            description="Sau khi lưu, thay đổi sẽ có hiệu lực ngay trong database. Website jupi-api tự động cập nhật sau tối đa 60 giây (Redis TTL)."
            type="info"
            showIcon
            className="text-xs"
          />

          {/* Locked Switch */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <div className="font-semibold text-gray-800 text-sm">Khóa cấu hình (Locked)</div>
              <div className="text-xs text-gray-500">
                Khi bật, cấu hình được đánh dấu là bảo vệ hệ thống và không thể bị xóa ngoài danh sách.
              </div>
            </div>
            <Switch
              checked={locked === 1}
              onChange={(checked) => setLocked(checked ? 1 : 0)}
              checkedChildren="Khóa"
              unCheckedChildren="Mở"
            />
          </div>

          {/* Sets Mode: Visual List + Raw JSON */}
          {isSetsGroup ? (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: "visual",
                  label: `Giao diện trực quan (${setItems.length} bộ từ)`,
                  children: (
                    <div className="flex flex-col gap-3">
                      {/* Search and Add Set */}
                      <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 flex flex-col gap-2">
                        <div className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                          <PlusOutlined className="text-primary" />
                          <span>Thêm bộ từ vào danh sách:</span>
                        </div>
                        <Select
                          showSearch
                          placeholder="Nhập tên hoặc ID bộ từ để tìm kiếm..."
                          filterOption={false}
                          onSearch={fetchSets}
                          onChange={handleAddSet}
                          loading={searchingSets}
                          options={setOptions}
                          value={null}
                          notFoundContent={searchingSets ? <Spin size="small" /> : "Không tìm thấy bộ từ"}
                          className="w-full"
                        />
                      </div>

                      {/* Sets Reorderable List */}
                      {setItems.length === 0 ? (
                        <Empty
                          description="Danh sách bộ từ đang trống. Hãy tìm kiếm và thêm bộ từ ở trên."
                          className="my-6"
                        />
                      ) : (
                        <div className="max-h-[380px] overflow-y-auto pr-1">
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <SortableContext
                              items={setItems.map((item) => item.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {setItems.map((item, index) => (
                                <SortableSetItem
                                  key={item.id}
                                  item={item}
                                  index={index}
                                  totalCount={setItems.length}
                                  onMoveUp={() => handleMoveUp(index)}
                                  onMoveDown={() => handleMoveDown(index)}
                                  onRemove={() => handleRemove(index)}
                                />
                              ))}
                            </SortableContext>
                          </DndContext>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "json",
                  label: "Mã JSON thô (Raw JSON)",
                  children: (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-mono">
                          Mảng ID các bộ từ hiển thị theo thứ tự
                        </span>
                        <Button
                          size="small"
                          icon={<FormatPainterOutlined />}
                          onClick={handleFormatJson}
                        >
                          Định dạng JSON
                        </Button>
                      </div>
                      <TextArea
                        rows={12}
                        value={jsonText}
                        onChange={(e) => handleRawJsonChange(e.target.value)}
                        className="font-mono text-xs bg-slate-900 text-green-400 p-3 rounded"
                        placeholder="[557, 2004, ...]"
                      />
                      {jsonError && (
                        <Alert message={jsonError} type="error" showIcon className="text-xs" />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            /* Generic JSON Editor for other groups */
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600 font-semibold">
                  Dữ liệu cấu hình (JSON Payload):
                </span>
                <Button
                  size="small"
                  icon={<FormatPainterOutlined />}
                  onClick={handleFormatJson}
                >
                  Định dạng JSON
                </Button>
              </div>
              <TextArea
                rows={14}
                value={jsonText}
                onChange={(e) => {
                  setJsonText(e.target.value)
                  try {
                    JSON.parse(e.target.value)
                    setJsonError(null)
                  } catch (err: any) {
                    setJsonError(`Lỗi cú pháp: ${err.message}`)
                  }
                }}
                className="font-mono text-xs bg-slate-900 text-green-400 p-3 rounded"
                placeholder="{ ... }"
              />
              {jsonError && (
                <Alert message={jsonError} type="error" showIcon className="text-xs" />
              )}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  )
}
